import React, { useState, useEffect, ChangeEvent } from 'react';
import { useSiteConfig } from '../../../app/providers/SiteConfigProvider';
import { useNotifications } from '../../../ui/notifications';
import { SiteConfig, Enlace } from "../../../api/Objetos";
import { AdminPageHeader } from '../ui/AdminPageHeader'; // Import AdminPageHeader
import { AdminContentCard } from '../ui/AdminContentCard'; // Import AdminContentCard
import { useTranslation } from 'react-i18next'; // Import useTranslation
// import './ConfigurationPage.css'; // Removed the custom CSS import

// Helper function to truncate URLs
const truncateUrl = (url: string, maxLength: number) => {
  if (url.length > maxLength) {
    return url.substring(0, maxLength) + '...';
  }
  return url;
};

const ConfigurationPage = () => {
  const { t } = useTranslation(); // Instantiate useTranslation
  const { config: globalConfig, setConfig: setGlobalConfig, error } = useSiteConfig();
  const [localConfig, setLocalConfig] = useState<SiteConfig>(globalConfig);
  const [isSaving, setIsSaving] = useState(false); // Added isSaving state
  const [hasChanges, setHasChanges] = useState(false); // Added hasChanges state
  const [logoNavbarName, setLogoNavbarName] = useState<string | null>(null);
  const [logoFooterName, setLogoFooterName] = useState<string | null>(null);
  const { showError, showSuccess } = useNotifications();

  useEffect(() => {
    setLocalConfig(globalConfig);
    setHasChanges(false); // Reset changes on global config update
  }, [globalConfig]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setLocalConfig((prevConfig: SiteConfig) => ({
      ...prevConfig,
      [name]: value,
    }));
    setHasChanges(true); // Mark changes
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>, fieldName: 'logoNavbar' | 'logoFooter') => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (fieldName === 'logoNavbar') {
      setLogoNavbarName(file.name);
    } else {
      setLogoFooterName(file.name);
    }

    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImageUrl = reader.result as string;
        setLocalConfig((prevConfig) => ({
          ...prevConfig,
          [fieldName]: newImageUrl,
        }));
        showSuccess(t('configPage.imageUploadSuccess'), t('configPage.imageUploadDescription', { fieldName }));
        setHasChanges(true); // Mark changes
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      showError(t('configPage.imageUploadError'), t('configPage.imageUploadErrorDescription', { message: err.message }));
      console.error("Error uploading image:", err);
    }
  };

  const handleEnlaceChange = (index: number, field: 'nombre' | 'url', value: string) => {
    setLocalConfig((prevConfig: SiteConfig) => {
      const newEnlaces = [...prevConfig.enlaces];
      newEnlaces[index] = { ...newEnlaces[index], [field]: value };
      return {
        ...prevConfig,
        enlaces: newEnlaces,
      };
    });
    setHasChanges(true); // Mark changes
  };

  const handleAddEnlace = () => {
    setLocalConfig((prevConfig: SiteConfig) => ({
      ...prevConfig,
      enlaces: [...prevConfig.enlaces, { nombre: '', url: '' }],
    }));
    setHasChanges(true); // Mark changes
  };

  const handleRemoveEnlace = (index: number) => {
    setLocalConfig((prevConfig: SiteConfig) => ({
      ...prevConfig,
      enlaces: prevConfig.enlaces.filter((_: Enlace, i: number) => i !== index),
    }));
    setHasChanges(true); // Mark changes
  };

  const handleSave = async () => {
    setIsSaving(true); // Set saving state to true
    try {
      await setGlobalConfig(localConfig);
      setHasChanges(false); // Reset changes after saving
      showSuccess(t('configPage.saveSuccess'), t('configPage.saveSuccessDescription'));
    } catch (err) {
      showError(t('manejadorErrores.saveError'), t('manejadorErrores.unexpectedError'));
      console.error("Error inesperado al guardar la configuración:", err);
    } finally {
      setIsSaving(false); // Set saving state to false
    }
  };

  return (
      <div className="adminPageWrap"> {/* Using adminPageWrap for consistent page layout */}
        <AdminPageHeader
            eyebrow={t('configPage.eyebrow')}
            title={t('configPage.title')}
            description={t('configPage.description')}
            actions={
              <button onClick={handleSave} className="btn btnPrimary" disabled={!hasChanges || isSaving}>
                {isSaving ? t('configPage.saving') : t('configPage.save')}
              </button>
            }
        />

        <AdminContentCard>
          <div className="stackLg">
            <section className="sectionCard"> {/* Using sectionCard for consistent section styling */}
              <h2>{t('configPage.navbar.title')}</h2>
              <div className="label"> {/* Using label as a container for form fields */}
                <span>{t('configPage.navbar.logo')}</span>
                {localConfig.logoNavbar && (
                    <div className="image-preview" style={{ marginBottom: '1rem', border: '1px solid var(--line)', borderRadius: '5px', padding: '0.5rem', display: 'inline-block', backgroundColor: 'var(--surface-muted)' }}>
                      <img src={localConfig.logoNavbar} alt={t('configPage.navbar.logoPreview')} style={{ maxWidth: '150px', maxHeight: '150px', display: 'block', borderRadius: '3px' }} />
                    </div>
                )}
                <div className="inlineActions">
                  <input
                      id="logoNavbar-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logoNavbar')}
                      style={{ display: 'none' }}
                  />
                  <label htmlFor="logoNavbar-upload" className="btn btnSecondary">
                    {t('configPage.selectFile')}
                  </label>
                  {logoNavbarName && <span className="muted">{logoNavbarName}</span>}
                </div>
                <p className="muted">{t('configPage.currentUrl')}: {truncateUrl(localConfig.logoNavbar, 100)}</p>
              </div>
              <div className="label">
                <span>{t('configPage.navbar.titleLabel')}</span>
                <input
                    type="text"
                    name="tituloNavbar"
                    value={localConfig.tituloNavbar}
                    onChange={handleInputChange}
                    className="input"
                />
              </div>
              <div className="label">
                <span>{t('configPage.navbar.subtitleLabel')}</span>
                <input
                    type="text"
                    name="subtituloNavbar"
                    value={localConfig.subtituloNavbar}
                    onChange={handleInputChange}
                    className="input"
                />
              </div>
            </section>

            <section className="sectionCard">
              <h2>{t('configPage.footer.title')}</h2>
              <div className="label">
                <span>{t('configPage.footer.logo')}</span>
                {localConfig.logoFooter && (
                    <div className="image-preview" style={{ marginBottom: '1rem', border: '1px solid var(--line)', borderRadius: '5px', padding: '0.5rem', display: 'inline-block', backgroundColor: 'var(--surface-muted)' }}>
                      <img src={localConfig.logoFooter} alt={t('configPage.footer.logoPreview')} style={{ maxWidth: '150px', maxHeight: '150px', display: 'block', borderRadius: '3px' }} />
                    </div>
                )}
                <div className="inlineActions">
                  <input
                      id="logoFooter-upload"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'logoFooter')}
                      style={{ display: 'none' }}
                  />
                  <label htmlFor="logoFooter-upload" className="btn btnSecondary">
                    {t('configPage.selectFile')}
                  </label>
                  {logoFooterName && <span className="muted">{logoFooterName}</span>}
                </div>
                <p className="muted">{t('configPage.currentUrl')}: {truncateUrl(localConfig.logoFooter, 100)}</p>
              </div>
              <div className="label">
                <span>{t('configPage.footer.address')}</span>
                <input
                    type="text"
                    name="direccion"
                    value={localConfig.direccion}
                    onChange={handleInputChange}
                    className="input"
                />
              </div>
              <div className="label">
                <span>{t('configPage.footer.phone')}</span>
                <input
                    type="tel"
                    name="telefono"
                    value={localConfig.telefono}
                    onChange={handleInputChange}
                    className="input"
                />
              </div>
              <div className="label">
                <span>{t('configPage.footer.email')}</span>
                <input
                    type="email"
                    name="correoElectronico"
                    value={localConfig.correoElectronico}
                    onChange={handleInputChange}
                    className="input"
                />
              </div>
            </section>

            <section className="sectionCard">
              <h3>{t('configPage.footer.linksTitle')}</h3>
              <div className="stack"> {/* Using stack for spacing between enlace items */}
                {localConfig.enlaces.map((enlace: Enlace, index: number) => (
                    <div key={index} className="inlineActions"> {/* Using inlineActions for horizontal alignment */}
                      <input
                          type="text"
                          placeholder={t('configPage.footer.linkNamePlaceholder')}
                          value={enlace.nombre}
                          onChange={(e) => handleEnlaceChange(index, 'nombre', e.target.value)}
                          className="input"
                      />
                      <input
                          type="text"
                          placeholder={t('configPage.footer.linkUrlPlaceholder')}
                          value={enlace.url} // Keep original value for editing, truncate for display if needed elsewhere
                          onChange={(e) => handleEnlaceChange(index, 'url', e.target.value)}
                          className="input"
                      />
                    </div>
                ))}
              </div>
            </section>
          </div>
        </AdminContentCard>
      </div>
  );
};

export default ConfigurationPage;